// (c) chaehongee. Confidential and proprietary.

const TOOL_DISPLAY_NAMES: Record<string, string> = {
    edit_image: 'nanobanana edit',
    nano_banana_pro_edit: 'nanobanana pro edit',
    replicate_lora: 'swap identity',
    replicate_topaz_upscale: 'topaz upscale',
    text_to_image: 'nanobanana',
    video_generation: 'kling video',
    soul_generate: 'higgsfield soul',
    qwen_multi_angle_edit: 'qwen multi angle',
    gpt_image_generate: 'GPT Image 2',
    grok_imagine: 'Grok Imagine',
    flux2_pro_text_to_image: 'FLUX.2 Pro',
    seedream_text_to_image: 'Seedream 4.5',
    recraft_v4_generate: 'Recraft V4',
    nano_banana_text_to_image: 'Nano Banana 2',
    flux_schnell_text_to_image: 'FLUX Schnell',
}

export function getToolDisplayName(toolName: string): string {
    return TOOL_DISPLAY_NAMES[toolName] || toolName
}

// Human, studio-voiced label for what a tool is *doing* — shown in the thread
// instead of the raw "calling tool: <model>" plumbing. Leads with the action;
// the model name rides along quietly as attribution (see ChatBubble).
const TOOL_ACTION_LABELS: Record<string, string> = {
    edit_image: 'Retouching',
    nano_banana_pro_edit: 'Retouching',
    replicate_lora: 'Swapping the face',
    replicate_topaz_upscale: 'Upscaling',
    text_to_image: 'Generating',
    video_generation: 'Animating',
    soul_generate: 'Generating',
    qwen_multi_angle_edit: 'Changing the angle',
    gpt_image_generate: 'Generating',
    grok_imagine: 'Generating',
    flux2_pro_text_to_image: 'Generating',
    seedream_text_to_image: 'Generating',
    recraft_v4_generate: 'Generating',
    nano_banana_text_to_image: 'Generating',
    flux_schnell_text_to_image: 'Generating',
}

export function getToolActionLabel(toolName: string): string {
    return TOOL_ACTION_LABELS[toolName] || 'Working'
}

// Clean brand/model name for the quiet attribution behind the action label —
// e.g. "Retouching · Nano Banana", not "Retouching · nanobanana edit". Distinct
// from getToolDisplayName (which keeps the verb, used in errors) so the thread
// never repeats the action word.
const TOOL_MODEL_NAMES: Record<string, string> = {
    edit_image: 'Nano Banana',
    nano_banana_pro_edit: 'Nano Banana Pro',
    replicate_lora: 'LoRA',
    replicate_topaz_upscale: 'Topaz',
    text_to_image: 'Nano Banana',
    video_generation: 'Kling',
    soul_generate: 'Higgsfield Soul',
    qwen_multi_angle_edit: 'Qwen',
    gpt_image_generate: 'GPT Image 2',
    grok_imagine: 'Grok',
    flux2_pro_text_to_image: 'FLUX.2 Pro',
    seedream_text_to_image: 'Seedream',
    recraft_v4_generate: 'Recraft',
    nano_banana_text_to_image: 'Nano Banana 2',
    flux_schnell_text_to_image: 'FLUX Schnell',
}

export function getToolModelName(toolName: string): string {
    return TOOL_MODEL_NAMES[toolName] || getToolDisplayName(toolName)
}

// Tool-progress notes are stored as `calling tool: <toolKey>` — an internal
// marker, never shown raw. Render them via parseToolNote().
export const TOOL_NOTE_PREFIX = 'calling tool:'

export function isToolNote(text: string): boolean {
    return text.startsWith(TOOL_NOTE_PREFIX)
}

export function parseToolNote(text: string): { action: string; model: string } {
    const key = text.slice(TOOL_NOTE_PREFIX.length).trim()
    return { action: getToolActionLabel(key), model: getToolModelName(key) }
}
